import type { CodeableConcept } from '@medplum/fhirtypes'
import {
  type CodingValue,
  codeableConcept,
  codeFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  SIGNATURE_REQUEST_TYPE,
  TASK_CATEGORY,
  TASK_TYPE,
} from '#src/codeSystems.ts'

export const TASK_CATEGORIES = {
  ENROLLMENT_TASK: codeableConcept({
    system: TASK_CATEGORY,
    code: 'enrollment-task',
    display: 'Enrollment Task',
  }),
  STANDARD_TASK: codeableConcept({
    system: TASK_CATEGORY,
    code: 'standard-task',
    display: 'Standard Task',
  }),
  SDR: codeableConcept({
    system: TASK_CATEGORY,
    code: 'sdr-task',
    display: 'SDR Task',
  }),
  SDR_ACTION: codeableConcept({
    system: TASK_CATEGORY,
    code: 'sdr-action-task',
    display: 'SDR Action Task',
  }),
} satisfies Record<string, CodeableConcept>

const standardTaskCodeableConcept = (coding: CodingValue): CodeableConcept =>
  codeableConcept([
    coding,
    ...((TASK_CATEGORIES.STANDARD_TASK?.coding ?? []) as CodingValue[]),
  ])

export const TASK_TYPES = {
  CREATE_BADGE: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'create-badge',
    display: 'Create Badge',
  }),
  SCHEDULE_APPOINTMENT: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'schedule-appointment',
    display: 'Schedule Appointment',
  }),
  APPOINTMENT_RECORD_RETRIEVAL: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'appointment-record-retrieval',
    display: 'Appointment Record Retrieval',
  }),
  REVIEW_EXTERNAL_AUTHORIZATION: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'review-external-authorization',
    display: 'Review External Authorization',
  }),
  REVIEW_LAB_RESULTS: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'review-lab-results',
    display: 'Review Lab Results',
  }),
  VENDOR_ORDER_PLACEMENT: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'vendor-order-placement',
    display: 'Vendor Order Placement',
  }),
  DOCUMENT_HOME_CARE_ORDER_SCHEDULE: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'document-home-care-order-schedule',
    display: 'Document Home Care Order Schedule',
  }),
  DOCUMENT_DME_ON_CARE_PLAN: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'document-dme-on-care-plan',
    display: 'Document DME on Care Plan',
  }),
  DOCUMENT_REVIEW_TASK: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'document-review-task',
    display: 'Document Review Task',
  }),
  QUEUED_EDITS_REVIEW: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'queued-edits-review',
    display: 'Queued Edits Review',
  }),
  SCHEDULING_REVALIDATION: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'scheduling-revalidation',
    display: 'Scheduling Revalidation',
  }),
  MEDICATION_REFILL: standardTaskCodeableConcept({
    system: TASK_TYPE,
    code: 'medication-refill',
    display: 'Medication Refill',
  }),
} satisfies Record<string, CodeableConcept>

export const SignatureTaskCode = {
  CARE_PLAN: 'signature-request-care-plan',
  CARE_PLAN_PARTICIPANT: 'signature-request-care-plan-participant',
  SDR: 'signature-request-sdr',
  NOTE: 'signature-request-note',
  ORDER: 'signature-request-order',
} as const
export type SignatureTaskCode =
  (typeof SignatureTaskCode)[keyof typeof SignatureTaskCode]

export const SIGNATURE_TASK_CODES = {
  [SignatureTaskCode.CARE_PLAN]: standardTaskCodeableConcept({
    system: SIGNATURE_REQUEST_TYPE,
    code: SignatureTaskCode.CARE_PLAN,
    display: 'Care Plan Signature',
  }),
  [SignatureTaskCode.CARE_PLAN_PARTICIPANT]: standardTaskCodeableConcept({
    system: SIGNATURE_REQUEST_TYPE,
    code: SignatureTaskCode.CARE_PLAN_PARTICIPANT,
    display: 'Care Plan Participant Signature',
  }),
  [SignatureTaskCode.SDR]: standardTaskCodeableConcept({
    system: SIGNATURE_REQUEST_TYPE,
    code: SignatureTaskCode.SDR,
    display: 'SDR Signature',
  }),
  [SignatureTaskCode.NOTE]: standardTaskCodeableConcept({
    system: SIGNATURE_REQUEST_TYPE,
    code: SignatureTaskCode.NOTE,
    display: 'Note Signature',
  }),
  [SignatureTaskCode.ORDER]: standardTaskCodeableConcept({
    system: SIGNATURE_REQUEST_TYPE,
    code: SignatureTaskCode.ORDER,
    display: 'Order Signature',
  }),
}

export const SIGNATURE_TASKS_TO_DISPLAY: SignatureTaskCode[] = [
  SignatureTaskCode.ORDER,
]

export const IGNORE_TASK_CODES = [
  TASK_CATEGORIES.ENROLLMENT_TASK,
  ...Object.values(SIGNATURE_TASK_CODES).filter(
    (concept) =>
      !concept.coding?.some(
        (c) =>
          SIGNATURE_TASKS_TO_DISPLAY.includes(c.code as SignatureTaskCode) &&
          c.system === SIGNATURE_REQUEST_TYPE
      )
  ),
].map((type) => codeFromCodeableConcept(type) as string)
